import type { Session } from "@supabase/supabase-js";
import { getAuthClient } from "./appcradle-auth";

const baseUrl = (import.meta.env.VITE_API_BASE_URL?.trim() || "").replace(/\/$/, "");

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly code: string, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

async function fetchWithToken(path: string, session: Session, init: RequestInit): Promise<Response> {
  return fetch(`${baseUrl}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...init.headers,
      Authorization: `Bearer ${session.access_token}`
    }
  });
}

export async function apiRequest<T>(path: string, session: Session, init: RequestInit = {}): Promise<T> {
  let response = await fetchWithToken(path, session, init);
  if (response.status === 401) {
    const client = await getAuthClient();
    if (client) {
      const refreshed = await client.auth.refreshSession();
      if (refreshed.data.session) response = await fetchWithToken(path, refreshed.data.session, init);
    }
  }
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: { code?: string; message?: string } } | null;
    throw new ApiError(response.status, payload?.error?.code ?? "request_failed", payload?.error?.message ?? "The request failed.");
  }
  return response.json() as Promise<T>;
}

export async function downloadExport(session: Session): Promise<void> {
  let response = await fetchWithToken("/api/v1/export", session, { method: "GET" });
  if (response.status === 401) {
    const client = await getAuthClient();
    if (client) {
      const refreshed = await client.auth.refreshSession();
      if (refreshed.data.session) response = await fetchWithToken("/api/v1/export", refreshed.data.session, { method: "GET" });
    }
  }
  if (!response.ok) throw new ApiError(response.status, "export_failed", "Your export could not be created.");
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `moneymap-export-${new Date().toISOString().slice(0, 10)}.json`;
  link.click();
  URL.revokeObjectURL(url);
}

export async function downloadContinuityReport(session: Session): Promise<void> {
  let response = await fetchWithToken("/api/v1/reports/continuity", session, { method: "GET" });
  if (response.status === 401) {
    const client = await getAuthClient();
    if (client) {
      const refreshed = await client.auth.refreshSession();
      if (refreshed.data.session) response = await fetchWithToken("/api/v1/reports/continuity", refreshed.data.session, { method: "GET" });
    }
  }
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: { message?: string } } | null;
    throw new ApiError(response.status, "continuity_report_failed", payload?.error?.message ?? "The continuity report could not be created.");
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `moneymap-continuity-${new Date().toISOString().slice(0, 10)}.html`;
  link.click();
  URL.revokeObjectURL(url);
}
